import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, RefreshCw, Search, ShieldCheck, KeyRound, Upload, X, FileSpreadsheet, Eye, UserPlus, Mail, Link2, School } from "lucide-react";
import StudentActivityModal from "@/components/StudentActivityModal";

const ROLE_LABEL = { admin: "Admin", siswa: "Siswa", orangtua: "Orang Tua" };
const ROLE_CLS = {
  admin: "bg-[#1A1C1A] text-white",
  siswa: "bg-[#4A6B53]/12 text-[#4A6B53]",
  orangtua: "bg-[#E25E3E]/12 text-[#E25E3E]",
};

function ImportModal({ onClose, onDone, mode = "siswa" }) {
  const isParent = mode === "orangtua";
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [resending, setResending] = useState(false);

  const submit = async () => {
    if (!file) { toast.error("Pilih file CSV/Excel dulu"); return; }
    setLoading(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    if (isParent) fd.append("default_role", "orangtua");
    try {
      const { data } = await api.post("/admin/users/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.created} akun dibuat · ${data.emailed ?? 0} email terkirim${data.linked ? ` · ${data.linked} tertaut ke siswa` : ""}`);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal impor");
    }
    setLoading(false);
  };

  const resendFailed = async () => {
    setResending(true);
    try {
      const { data } = await api.post("/admin/users/resend-welcome", {});
      toast.success(`${data.sent} email berhasil dikirim ulang`);
      setResult((r) => r ? { ...r, emailed: (r.emailed || 0) + data.sent, email_failed: data.failed || [] } : r);
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal kirim ulang email"); }
    setResending(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="import-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-heading font-bold text-lg text-[#1A1C1A] flex items-center gap-2"><FileSpreadsheet className={`h-5 w-5 ${isParent ? "text-[#E25E3E]" : "text-[#4A6B53]"}`} /> {isParent ? "Impor Orang Tua Massal" : "Impor Siswa Massal"}</h2>
          <button onClick={onClose} data-testid="close-import-btn" className="p-1 text-[#9C9A94] hover:text-[#1A1C1A]"><X className="h-5 w-5" /></button>
        </div>
        {isParent ? (
          <>
            <p className="text-sm text-[#5C5F5C] mb-3">Unggah file <b>CSV</b> atau <b>Excel (.xlsx)</b> dengan kolom: <code className="text-xs bg-[#F0ECE4] px-1.5 py-0.5 rounded">nama</code>, <code className="text-xs bg-[#F0ECE4] px-1.5 py-0.5 rounded">email</code>, dan <b>salah satu</b> dari <code className="text-xs bg-[#F0ECE4] px-1.5 py-0.5 rounded">email_anak</code> atau <code className="text-xs bg-[#F0ECE4] px-1.5 py-0.5 rounded">kode_anak</code> untuk menautkan ke akun siswa.</p>
            <p className="text-xs text-[#9C9A94] mb-4">Semua baris otomatis dibuat sebagai akun <b>orang tua</b> (kolom <code>role</code> tidak wajib). Setiap akun menerima email pantau-progres dengan <b>password sementara</b> <code className="text-xs bg-[#F0ECE4] px-1 py-0.5 rounded">Talenta2026!</code>. Orang tua otomatis <b>tertaut ke siswa</b> lewat <code>email_anak</code>/<code>kode_anak</code>. Email yang sudah terdaftar akan <b>dilewati</b>. Pastikan akun siswa sudah ada agar penautan berhasil.</p>
          </>
        ) : (
          <>
            <p className="text-sm text-[#5C5F5C] mb-3">Unggah file <b>CSV</b> atau <b>Excel (.xlsx)</b> dengan kolom: <code className="text-xs bg-[#F0ECE4] px-1.5 py-0.5 rounded">nama</code>, <code className="text-xs bg-[#F0ECE4] px-1.5 py-0.5 rounded">email</code>, <code className="text-xs bg-[#F0ECE4] px-1.5 py-0.5 rounded">kelas</code>, <code className="text-xs bg-[#F0ECE4] px-1.5 py-0.5 rounded">no_hp</code>, <code className="text-xs bg-[#F0ECE4] px-1.5 py-0.5 rounded">asal_sekolah</code>.</p>
            <p className="text-xs text-[#9C9A94] mb-4">Setiap akun baru otomatis menerima email berisi nama, email login, <b>password sementara</b> <code className="text-xs bg-[#F0ECE4] px-1 py-0.5 rounded">Talenta2026!</code> (seragam untuk semua), dan link ke aplikasi. Ingatkan untuk mengganti password setelah login pertama. Kolom opsional <b>role</b> (isi <code>orangtua</code>) membuat akun orang tua &mdash; mereka menerima email pantau-progres otomatis. Untuk baris orang tua, tambahkan kolom <code className="text-xs bg-[#F0ECE4] px-1 py-0.5 rounded">email_anak</code> atau <code className="text-xs bg-[#F0ECE4] px-1 py-0.5 rounded">kode_anak</code> agar otomatis <b>tertaut ke akun siswa</b>. Email yang sudah terdaftar akan dilewati. Jika ada email gagal terkirim, kamu bisa kirim ulang di bawah.</p>
          </>
        )}

        <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input"
          className="w-full text-sm text-[#5C5F5C] file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#1A1C1A] file:text-white file:text-sm mb-4" />

        <button onClick={submit} disabled={loading} data-testid="import-submit-btn"
          className="px-6 py-2.5 rounded-full bg-[#E25E3E] text-white font-medium hover:bg-[#C94F31] transition-colors inline-flex items-center gap-2 disabled:opacity-60">
          <Upload className="h-4 w-4" /> {loading ? "Mengimpor…" : (isParent ? "Impor Orang Tua" : "Impor Sekarang")}
        </button>

        {result && (
          <div className="mt-5 clay-card p-4" data-testid="import-result">
            <div className="flex flex-wrap gap-4 text-sm mb-2">
              <span className="text-[#4A6B53]">Dibuat: <b>{result.created}</b></span>
              <span className="text-[#3E6B8A]">Email terkirim: <b>{result.emailed ?? 0}</b></span>
              {result.linked > 0 && <span className="text-[#7A4FA0]">Tertaut ke anak: <b>{result.linked}</b></span>}
              <span className="text-[#9C9A94]">Dilewati: <b>{result.skipped}</b></span>
              <span className="text-[#D9381E]">Email gagal: <b>{result.email_failed?.length || 0}</b></span>
            </div>
            {result.link_failed?.length > 0 && (
              <div className="mt-1 mb-1" data-testid="import-link-failed">
                <p className="text-xs text-[#B4801F] mb-1">Orang tua gagal ditautkan (siswa tidak ditemukan):</p>
                <ul className="text-xs text-[#B4801F] list-disc pl-4 space-y-0.5 max-h-24 overflow-auto">
                  {result.link_failed.map((f, i) => <li key={i}>{f.parent} → {f.ref}</li>)}
                </ul>
              </div>
            )}
            {result.email_failed?.length > 0 && (
              <div className="mt-2" data-testid="import-email-failed">
                <p className="text-xs text-[#D9381E] mb-1">Email gagal terkirim ke:</p>
                <ul className="text-xs text-[#D9381E] list-disc pl-4 space-y-0.5 max-h-28 overflow-auto">
                  {result.email_failed.map((f, i) => <li key={i}>{f.email} — {f.error}</li>)}
                </ul>
                <button onClick={resendFailed} disabled={resending} data-testid="resend-welcome-btn"
                  className="mt-2 px-4 py-2 rounded-full bg-[#3E6B8A] text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-60">
                  <RefreshCw className={`h-3.5 w-3.5 ${resending ? "animate-spin" : ""}`} /> {resending ? "Mengirim ulang…" : "Kirim ulang email gagal"}
                </button>
              </div>
            )}
            {result.errors?.length > 0 && (
              <ul className="text-xs text-[#D9381E] list-disc pl-4 space-y-0.5 mt-2">{result.errors.map((er, i) => <li key={i}>{er}</li>)}</ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AddAdminModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Nama dan email wajib diisi"); return; }
    if (form.password.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    setLoading(true);
    try {
      await api.post("/admin/users/create-admin", form);
      toast.success(`Admin "${form.name}" berhasil dibuat`);
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat admin"); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="add-admin-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading font-bold text-lg text-[#1A1C1A] flex items-center gap-2"><UserPlus className="h-5 w-5 text-[#1A1C1A]" /> Tambah Admin</h2>
          <button onClick={onClose} data-testid="close-add-admin-btn" className="p-1 text-[#9C9A94] hover:text-[#1A1C1A]"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm text-[#5C5F5C] mb-4">Buat akun baru dengan role <b>Admin</b>. Akun ini punya akses penuh ke panel admin.</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-[#5C5F5C]">Nama</label>
            <input value={form.name} onChange={set("name")} data-testid="admin-name-input" placeholder="Nama lengkap"
              className="w-full h-10 px-3 rounded-xl border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#5C5F5C]">Email</label>
            <input type="email" value={form.email} onChange={set("email")} data-testid="admin-email-input" placeholder="email@contoh.com"
              className="w-full h-10 px-3 rounded-xl border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#5C5F5C]">Password</label>
            <input type="text" value={form.password} onChange={set("password")} data-testid="admin-password-input" placeholder="Minimal 6 karakter"
              className="w-full h-10 px-3 rounded-xl border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm mt-1" />
          </div>
        </div>
        <button onClick={submit} disabled={loading} data-testid="add-admin-submit-btn"
          className="mt-5 w-full py-2.5 rounded-full bg-[#1A1C1A] text-white font-medium hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <UserPlus className="h-4 w-4" /> {loading ? "Menyimpan…" : "Buat Admin"}
        </button>
      </div>
    </div>
  );
}

export default function KelolaAkun() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [resetId, setResetId] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [showImport, setShowImport] = useState(false);
  const [showImportParent, setShowImportParent] = useState(false);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [activityId, setActivityId] = useState(null);
  const [emailSummary, setEmailSummary] = useState(null);
  const [autoLinkBusy, setAutoLinkBusy] = useState(false);
  const [autoLinkResult, setAutoLinkResult] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [bulkSchool, setBulkSchool] = useState("");

  const loadEmailSummary = async () => {
    try {
      const { data } = await api.get("/admin/email/summary");
      setEmailSummary(data);
    } catch { /* ignore */ }
  };

  const autoLinkByName = async () => {
    if (!window.confirm('Tautkan otomatis SEMUA orang tua ke siswa berdasarkan kecocokan NAMA?\n\nContoh: "ORTU BRILLIANT ISAAC MATTHEW" akan ditautkan ke siswa "BRILLIANT ISAAC MATTHEW". Hanya menautkan yang cocok persis & unik.')) return;
    setAutoLinkBusy(true); setAutoLinkResult(null);
    try {
      const { data } = await api.post("/admin/parents/auto-link-by-name", {});
      setAutoLinkResult(data);
      toast.success(`${data.linked} ortu baru tertaut · ${data.already_linked} sudah tertaut · ${data.unmatched.length} tidak cocok · ${data.ambiguous.length} ganda`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menautkan otomatis"); }
    setAutoLinkBusy(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users || []);
      setSelected(new Set());
      try { const { data: pd } = await api.get("/admin/school-profiles"); setProfiles(pd.profiles || []); } catch { /* ignore */ }
    } catch { toast.error("Gagal memuat daftar akun"); }
    setLoading(false);
    loadEmailSummary();
  };

  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    setBusyId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Akun "${u.name}" dihapus`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus akun"); }
    setBusyId(null); setConfirmId(null);
  };

  const doReset = async (u) => {
    if (newPw.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/reset-password`, { password: newPw });
      toast.success(`Password "${u.name}" berhasil diubah`);
      setResetId(null); setNewPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal reset password"); }
    setBusyId(null);
  };

  const assignSchool = async (ids, pid) => {
    try {
      await api.post("/admin/users/assign-school", { user_ids: ids, school_profile_id: pid });
      setUsers((prev) => prev.map((x) => ids.includes(x.id) ? { ...x, school_profile_id: pid || null } : x));
      toast.success(pid ? "Sekolah/Mitra ditetapkan" : "Sekolah/Mitra dikosongkan");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menetapkan sekolah"); }
  };

  const bulkAssignSchool = async () => {
    if (!selected.size) { toast.info("Pilih siswa dulu"); return; }
    await assignSchool([...selected], bulkSchool || null);
  };

  const filtered = users.filter((u) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
  });
  const selectable = filtered.filter((u) => u.role !== "admin" && u.id !== user?.id);
  const allSelected = selectable.length > 0 && selectable.every((u) => selected.has(u.id));

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((prev) => allSelected ? new Set() : new Set(selectable.map((u) => u.id)));

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Hapus ${selected.size} akun terpilih? Tindakan ini tidak bisa dibatalkan.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { ids: [...selected] });
      toast.success(`${data.deleted} akun dihapus`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal hapus massal"); }
    setBulkBusy(false);
  };

  const bulkReset = async () => {
    if (!selected.size) return;
    const pw = window.prompt(`Password baru untuk ${selected.size} akun terpilih (min 6 karakter):`, "Talenta2026!");
    if (pw == null) return;
    if (pw.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-reset", { ids: [...selected], password: pw });
      toast.success(`${data.reset} password direset`);
      setSelected(new Set());
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal reset massal"); }
    setBulkBusy(false);
  };

  const counts = users.reduce((a, u) => { a[u.role] = (a[u.role] || 0) + 1; return a; }, {});

  const [notifyBusy, setNotifyBusy] = useState(false);
  const [notifyProg, setNotifyProg] = useState(null);
  const notifyAllStudents = async () => {
    const n = counts.siswa || 0;
    if (!window.confirm(`Kirim email "akun siap" ke siswa yang BELUM menerima email (dari total ${n} siswa)? Password mereka diset ke Talenta2026! agar kredensial valid. Pengiriman berjalan bertahap di latar belakang. Jika kuota email penuh, sisanya ditandai "pending" dan bisa dilanjutkan dengan menekan tombol ini lagi nanti.`)) return;
    setNotifyBusy(true);
    setNotifyProg(null);
    try {
      const { data } = await api.post("/admin/email/notify-all-students", {});
      if (!data.started) { toast.info("Tidak ada siswa untuk dikirimi email."); setNotifyBusy(false); return; }
      toast.success(`Pengiriman dimulai untuk ${data.total} siswa. Berjalan di latar belakang…`);
      const poll = setInterval(async () => {
        try {
          const { data: s } = await api.get("/admin/email/notify-status");
          setNotifyProg({ total: s.total, sent: s.sent, failed: s.failed });
          if (!s.running) {
            clearInterval(poll);
            setNotifyBusy(false);
            loadEmailSummary();
            if (s.rate_limited && s.sent === 0) {
              toast.error(`Kuota email layanan sedang penuh — 0 terkirim. Coba lagi nanti (±1 jam). Sisa siswa ditandai "pending" & akan dilanjut saat tombol ditekan lagi.`);
            } else if (s.rate_limited) {
              toast.warning(`${s.sent} terkirim, lalu kuota email penuh. Sisanya "pending" — tekan lagi nanti untuk melanjutkan.`);
            } else {
              toast.success(`Selesai: ${s.sent} email terkirim${s.failed ? ` · ${s.failed} gagal` : ""}`);
            }
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memulai pengiriman email");
      setNotifyBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto" data-testid="kelola-akun-page">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h1 className="font-heading font-black text-3xl text-[#1A1C1A]">Kelola Akun</h1>
          <div className="flex items-center gap-2">
            <button onClick={notifyAllStudents} disabled={notifyBusy} data-testid="notify-all-students-btn"
              className="px-4 py-2 rounded-full bg-[#E25E3E] text-white text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[#c94e30] disabled:opacity-50">
              <Mail className="h-4 w-4" /> {notifyBusy ? (notifyProg ? `Mengirim… ${notifyProg.sent + notifyProg.failed}/${notifyProg.total}` : "Memulai…") : "Email Semua Siswa"}
            </button>
            <button onClick={() => setShowAddAdmin(true)} data-testid="open-add-admin-btn"
              className="px-4 py-2 rounded-full bg-[#1A1C1A] text-white text-sm font-medium inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity">
              <UserPlus className="h-4 w-4" /> Tambah Admin
            </button>
            <button onClick={() => setShowImport(true)} data-testid="open-import-btn"
              className="px-4 py-2 rounded-full bg-[#4A6B53] text-white text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[#3d5946]">
              <FileSpreadsheet className="h-4 w-4" /> Impor Siswa
            </button>
            <button onClick={() => setShowImportParent(true)} data-testid="open-import-parent-btn"
              className="px-4 py-2 rounded-full bg-[#7A4FA0] text-white text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[#684490]">
              <FileSpreadsheet className="h-4 w-4" /> Impor Orang Tua
            </button>
            <button onClick={autoLinkByName} disabled={autoLinkBusy} data-testid="auto-link-by-name-btn"
              className="px-4 py-2 rounded-full bg-[#3E6B8A] text-white text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[#345b76] disabled:opacity-50">
              <Link2 className="h-4 w-4" /> {autoLinkBusy ? "Menautkan…" : "Tautkan Otomatis"}
            </button>
          </div>
        </div>
        <p className="text-[#5C5F5C] mb-4">Lihat, impor, hapus & reset akun. Akun admin dan akun Anda sendiri tidak bisa diubah.</p>

        <div className="flex flex-wrap gap-2 mb-4 text-sm">
          <span className="px-3 py-1 rounded-full bg-[#F0ECE4] text-[#1A1C1A]">Total: <b>{users.length}</b></span>
          <span className="px-3 py-1 rounded-full bg-[#4A6B53]/12 text-[#4A6B53]">Siswa: <b>{counts.siswa || 0}</b></span>
          <span className="px-3 py-1 rounded-full bg-[#E25E3E]/12 text-[#E25E3E]">Orang Tua: <b>{counts.orangtua || 0}</b></span>
          <span className="px-3 py-1 rounded-full bg-[#1A1C1A] text-white">Admin: <b>{counts.admin || 0}</b></span>
        </div>

        {emailSummary && emailSummary.total > 0 && (
          <div className="clay-card p-3 mb-4" data-testid="email-summary">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-[#1A1C1A] mr-1 inline-flex items-center gap-1.5"><Mail className="h-4 w-4 text-[#E25E3E]" /> Status email siswa:</span>
              <span className="px-3 py-1 rounded-full bg-[#4A6B53]/12 text-[#4A6B53]" data-testid="email-sent-count">Terkirim: <b>{emailSummary.sent}</b></span>
              <span className="px-3 py-1 rounded-full bg-[#E2A03E]/15 text-[#B4801F]" data-testid="email-pending-count">Pending: <b>{emailSummary.pending}</b></span>
              <span className="px-3 py-1 rounded-full bg-[#D9381E]/12 text-[#D9381E]" data-testid="email-failed-count">Gagal: <b>{emailSummary.failed}</b></span>
              <span className="px-3 py-1 rounded-full bg-[#F0ECE4] text-[#5C5F5C]" data-testid="email-belum-count">Belum dikirim: <b>{emailSummary.belum}</b></span>
            </div>
            {(emailSummary.pending > 0 || emailSummary.belum > 0) && (
              <p className="text-xs text-[#9C9A94] mt-2">Ada <b>{emailSummary.pending + emailSummary.belum}</b> siswa yang belum menerima email. Tekan <b>"Email Semua Siswa"</b> untuk mengirim/melanjutkan (yang sudah terkirim tidak dikirim ulang).</p>
            )}
          </div>
        )}

        {autoLinkResult && (
          <div className="clay-card p-3 mb-4" data-testid="auto-link-result">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-[#1A1C1A] mr-1 inline-flex items-center gap-1.5"><Link2 className="h-4 w-4 text-[#3E6B8A]" /> Hasil tautkan otomatis:</span>
              <span className="px-3 py-1 rounded-full bg-[#4A6B53]/12 text-[#4A6B53]">Tertaut baru: <b>{autoLinkResult.linked}</b></span>
              <span className="px-3 py-1 rounded-full bg-[#F0ECE4] text-[#5C5F5C]">Sudah tertaut: <b>{autoLinkResult.already_linked}</b></span>
              <span className="px-3 py-1 rounded-full bg-[#D9381E]/12 text-[#D9381E]">Tidak cocok: <b>{autoLinkResult.unmatched.length}</b></span>
              <span className="px-3 py-1 rounded-full bg-[#E2A03E]/15 text-[#B4801F]">Nama ganda: <b>{autoLinkResult.ambiguous.length}</b></span>
            </div>
            {autoLinkResult.unmatched.length > 0 && (
              <div className="mt-2" data-testid="auto-link-unmatched">
                <p className="text-xs text-[#D9381E] mb-1">Orang tua tanpa siswa cocok (perbaiki nama siswa/ortu lalu ulangi):</p>
                <ul className="text-xs text-[#5C5F5C] list-disc pl-4 space-y-0.5 max-h-32 overflow-auto">
                  {autoLinkResult.unmatched.map((u, i) => <li key={i}><b>{u.parent}</b> → cari siswa: "{u.dicari}"</li>)}
                </ul>
              </div>
            )}
            {autoLinkResult.ambiguous.length > 0 && (
              <div className="mt-2" data-testid="auto-link-ambiguous">
                <p className="text-xs text-[#B4801F] mb-1">Nama siswa ganda (tautkan manual untuk ini):</p>
                <ul className="text-xs text-[#5C5F5C] list-disc pl-4 space-y-0.5 max-h-24 overflow-auto">
                  {autoLinkResult.ambiguous.map((a, i) => <li key={i}><b>{a.parent}</b> → "{a.dicari}" ({a.jumlah} siswa)</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-2 h-10 px-3 rounded-xl border border-[#E5E0D8] focus-within:border-[#E25E3E] bg-white flex-1">
            <Search className="h-4 w-4 text-[#9C9A94]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-search-input"
              placeholder="Cari nama atau email…" className="flex-1 outline-none text-sm bg-transparent" />
          </div>
          <button onClick={load} data-testid="refresh-users-btn"
            className="h-10 px-4 rounded-xl border border-[#E5E0D8] text-sm text-[#5C5F5C] inline-flex items-center gap-1.5 hover:bg-[#F7F4EE]">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Muat ulang
          </button>
        </div>

        {selectable.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-3 px-4 py-2.5 rounded-xl bg-[#F7F4EE] border border-[#E5E0D8]" data-testid="bulk-bar">
            <label className="flex items-center gap-2 text-sm text-[#1A1C1A] cursor-pointer">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all-checkbox" className="h-4 w-4 accent-[#E25E3E]" />
              Pilih semua ({selectable.length})
            </label>
            <span className="text-sm text-[#5C5F5C]">{selected.size} dipilih</span>
            {profiles.length > 0 && (
              <div className="flex items-center gap-1.5">
                <select value={bulkSchool} onChange={(e) => setBulkSchool(e.target.value)} data-testid="bulk-school-select"
                  className="h-8 text-xs rounded-lg border border-[#E5E0D8] bg-white px-2 outline-none focus:border-[#E25E3E]">
                  <option value="">— Kosongkan —</option>
                  {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <button onClick={bulkAssignSchool} disabled={!selected.size} data-testid="bulk-assign-school-btn"
                  className="text-xs px-3 py-1.5 rounded-full bg-[#3E6B8A] text-white font-medium disabled:opacity-40 inline-flex items-center gap-1"><School className="h-3.5 w-3.5" /> Set Sekolah</button>
              </div>
            )}
            <div className="flex-1" />
            <button onClick={bulkReset} disabled={!selected.size || bulkBusy} data-testid="bulk-reset-btn"
              className="text-xs px-3 py-1.5 rounded-full bg-[#4A6B53] text-white font-medium disabled:opacity-40 inline-flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> Reset password</button>
            <button onClick={bulkDelete} disabled={!selected.size || bulkBusy} data-testid="bulk-delete-btn"
              className="text-xs px-3 py-1.5 rounded-full bg-[#D9381E] text-white font-medium disabled:opacity-40 inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Hapus</button>
          </div>
        )}

        <div className="clay-card divide-y divide-[#F0ECE4]">
          {loading && <div className="p-5 text-sm text-[#9C9A94]">Memuat…</div>}
          {!loading && filtered.length === 0 && <div className="p-5 text-sm text-[#9C9A94]">Tidak ada akun cocok.</div>}
          {filtered.map((u) => {
            const isSelf = u.id === user?.id;
            const isAdmin = u.role === "admin";
            const locked = isSelf || isAdmin;
            return (
              <div key={u.id} className="flex items-center gap-3 p-4" data-testid={`user-row-${u.id}`}>
                {!locked ? (
                  <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} data-testid={`select-${u.id}`} className="h-4 w-4 accent-[#E25E3E] shrink-0" />
                ) : <span className="w-4 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#1A1C1A] truncate">{u.name || "-"}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_CLS[u.role] || "bg-[#F0ECE4]"}`}>{ROLE_LABEL[u.role] || u.role}</span>
                    {u.kelas ? <span className="text-xs text-[#9C9A94]">Kelas {u.kelas}</span> : null}
                  </div>
                  <div className="text-xs text-[#9C9A94] truncate">{u.email}</div>
                  {u.role === "siswa" && (
                    <select value={u.school_profile_id || ""} onChange={(e) => assignSchool([u.id], e.target.value || null)}
                      data-testid={`school-select-${u.id}`}
                      className="mt-1 h-7 text-xs rounded-lg border border-[#E5E0D8] bg-white px-2 outline-none focus:border-[#E25E3E] max-w-[220px]">
                      <option value="">— Sekolah/Mitra —</option>
                      {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  )}
                </div>
                {locked ? (
                  <span className="text-xs text-[#9C9A94] inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> {isSelf ? "Akun Anda" : "Dilindungi"}</span>
                ) : resetId === u.id ? (
                  <div className="flex items-center gap-2">
                    <input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid={`reset-input-${u.id}`}
                      placeholder="Password baru (min 6)" autoFocus className="h-8 px-2 text-xs rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E] w-44" />
                    <button onClick={() => doReset(u)} disabled={busyId === u.id || newPw.length < 6} data-testid={`confirm-reset-${u.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-[#4A6B53] text-white font-medium disabled:opacity-50">{busyId === u.id ? "Menyimpan…" : "Simpan"}</button>
                    <button onClick={() => { setResetId(null); setNewPw(""); }} className="text-xs px-3 py-1.5 rounded-full border border-[#E5E0D8] text-[#5C5F5C]">Batal</button>
                  </div>
                ) : confirmId === u.id ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#D9381E]">Hapus?</span>
                    <button onClick={() => remove(u)} disabled={busyId === u.id} data-testid={`confirm-delete-${u.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-[#D9381E] text-white font-medium disabled:opacity-50">{busyId === u.id ? "Menghapus…" : "Ya, hapus"}</button>
                    <button onClick={() => setConfirmId(null)} className="text-xs px-3 py-1.5 rounded-full border border-[#E5E0D8] text-[#5C5F5C]">Batal</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setActivityId(u.id)} data-testid={`view-activity-${u.id}`}
                      title="Lihat profil & aktivitas siswa" className="px-2.5 py-1.5 rounded-full bg-[#1A1C1A] text-white text-xs font-medium inline-flex items-center gap-1 hover:opacity-90 transition-opacity"><Eye className="h-3.5 w-3.5" /> Profil</button>
                    <button onClick={() => { setResetId(u.id); setNewPw(""); setConfirmId(null); }} data-testid={`reset-user-${u.id}`}
                      title="Reset password" className="p-2 rounded-lg text-[#4A6B53] hover:bg-[#4A6B53]/10 transition-colors"><KeyRound className="h-4 w-4" /></button>
                    <button onClick={() => { setConfirmId(u.id); setResetId(null); }} data-testid={`delete-user-${u.id}`}
                      title="Hapus akun" className="p-2 rounded-lg text-[#D9381E] hover:bg-[#D9381E]/10 transition-colors"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={load} />}
      {showImportParent && <ImportModal mode="orangtua" onClose={() => setShowImportParent(false)} onDone={load} />}
      {showAddAdmin && <AddAdminModal onClose={() => setShowAddAdmin(false)} onDone={load} />}
      {activityId && <StudentActivityModal userId={activityId} onClose={() => setActivityId(null)} />}
    </AppShell>
  );
}
